import { Component, Input, OnInit } from '@angular/core';
import { Site } from 'src/app/shared/data/data.generated';
import { Filter, Table } from 'src/app/shared/services/api/table';
import { Router } from '@angular/router';
import {
  GetCustomTypeIcon,
  GetCustomTypeIconWidth,
  GetCustomTypeText,
} from 'src/app/shared/data/product-types';
import { ImagesService } from 'src/app/shared/services/images.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { FormBuilder } from '@angular/forms';
import { UtilityService } from 'src/app/shared/services/utility.service';
import {
  ActiveChipFilters,
  Chip,
  HrtFilterChipsComponent,
} from 'src/app/shared/components/hrt-filter-chips/hrt-filter-chips.component';
import {
  ColumnDefinitionV3,
  PageContentLayoutComponent,
  PageHeaderModule,
  SearchBarV2Module,
  TableServiceV3,
  TableV3Component,
} from '@gea/digital-ui-lib';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'gea-site-table',
  templateUrl: './site-table.component.html',
  styleUrls: ['./site-table.component.scss'],
  standalone: true,
  imports: [
    PageHeaderModule,
    SearchBarV2Module,
    HrtFilterChipsComponent,
    TableV3Component,
    PageContentLayoutComponent,
    MatIconModule,
  ],
})
export class SiteTableComponent implements OnInit {
  public getCustomTypeText = GetCustomTypeText;
  public getCustomTypeIcon = GetCustomTypeIcon;
  public getCustomTypeIconWidth = GetCustomTypeIconWidth;

  public pdfImage: string = this.images.Icons.FormatPdf;
  annunStatuses: any = {};

  tableId: string = 'hrtc-sites-table';
  searchPlaceholder = 'Search Sites';
  destroy$ = new Subject<void>();
  searchInput = new Subject<string>();
  count: number = 0;
  sortKey?: string;
  sortOrder?: string;
  statusFilters: string[] = [];
  page: number = 0;
  pageSize: number = 10;
  siteSearch?: string;
  sitesResponse: Site[] = [];
  isTableLoading: boolean = true;
  filters: Filter = {};
  activeChipFilters: ActiveChipFilters = { searchText: '', filters: {} };
  searchForm = this.fb.group({
    search: [''],
  });

  public columnDefinitions: ColumnDefinitionV3[] = [
    {
      displayName: 'Name',
      key: 'Name',
      sortable: true,
      minWidth: 200,
    },
    {
      displayName: 'Address',
      key: 'Address',
      sortable: true,
      minWidth: 200,
    },
    {
      displayName: 'City',
      key: 'City',
      sortable: true,
      minWidth: 200,
    },
    {
      displayName: 'State',
      key: 'State',
      sortable: true,
      minWidth: 200,
    },
    {
      displayName: 'Country',
      key: 'Country',
      sortable: true,
      minWidth: 200,
    },
    {
      displayName: 'Zip',
      key: 'Zip',
      sortable: true,
      minWidth: 300,
    },
    {
      displayName: 'Account Numbers',
      key: 'AxAccountNumbers',
      sortable: true,
      minWidth: 200,
    },
    {
      displayName: 'Project Numbers',
      key: 'ProjectNumbers',
      sortable: false,
      minWidth: 200,
    },
  ];
  public infoImage: string = this.images.Icons.Information;

  constructor(
    private router: Router,
    private images: ImagesService,
    private api: ApiService,
    private fb: FormBuilder,
    private utilityService: UtilityService,
    private tableService: TableServiceV3,
  ) {}

  ngOnInit() {
    this.handleHeaderClick();
    this.handleSearchChanged();
  }

  private handleHeaderClick() {
    this.tableService
      .getFilterTableSettings(this.tableId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        if (!settings?.columns) return;
        if (settings?.columns) {
          this.filters = this.buildBackendFiltersFromColumns(
            settings.columns,
            this.columnDefinitions,
          );
        }
        if (settings?.page !== undefined) {
          this.page = settings.page;
        }
        this.pageSize = settings?.pageSize ?? this.pageSize;
        const sortedColumn = Object.keys(settings.columns).find(
          (key) => settings.columns[key].sort ?? '',
        );

        if (sortedColumn) {
          this.sortKey = sortedColumn;
          if (settings.columns[sortedColumn].sort) {
            this.sortOrder =
              settings.columns[sortedColumn].sort === 'desc' ? 'True' : 'False';
          } else {
            this.sortOrder = '';
          }
        }

        this.activeChipFilters.filters = settings?.columns
          ? settings.columns
          : this.activeChipFilters.filters;

        this.fetchSitesList();
      });
  }

  openSite(id: number) {
    this.router.navigate(['/site-details', { id: id }]);
  }

  searchValueChanged(newValue: string): void {
    this.searchInput.next(newValue);
  }

  private handleSearchChanged(): void {
    this.searchInput.pipe(debounceTime(500)).subscribe((searchTerm: string) => {
      if (searchTerm.length >= 3 || searchTerm.length == 0) {
        this.siteSearch = searchTerm;
        this.utilityService.setTableSearchSetting(
          this.tableId,
          this.siteSearch,
        );
        this.page = 0; // Reset to first page on new table search
        this.fetchSitesList();
      }
    });
  }

  onChipRemoved(chip: Chip) {
    if (chip && chip.chipKey === 'clearAll') {
      // Clear all filters
      this.activeChipFilters = { searchText: '', filters: {} };
      this.siteSearch = '';
      this.sortKey = '';
      this.sortOrder = '';
      this.filters = {};
      this.utilityService.setTableSearchSetting(this.tableId, '');
      this.searchForm.get('search')?.setValue('');
      this.tableService.setFilterTableSettings(this.tableId, {
        columns: this.activeChipFilters.filters,
        page: this.page,
        pageSize: this.pageSize,
      });
    } else if (
      chip &&
      chip.chipKey === 'searchText' &&
      chip.chipValue !== '' &&
      this.activeChipFilters.searchText === chip.chipValue
    ) {
      // Clear site search
      this.siteSearch = '';
      this.utilityService.setTableSearchSetting(this.tableId, '');
      this.activeChipFilters.searchText = '';
      this.tableService.setFilterTableSettings(this.tableId, {
        columns: this.activeChipFilters.filters,
        page: this.page,
        pageSize: this.pageSize,
      });
      this.searchForm.get('search')?.setValue('');
    } else {
      if (chip && chip.chipKey !== 'searchText' && chip.chipValue !== '') {
        // 1. If chipValue is 'asc' or 'desc', remove "sort chip" and update documents
        if (chip.chipValue === 'asc' || chip.chipValue === 'desc') {
          if (this.activeChipFilters.filters[chip.chipKey]) {
            this.activeChipFilters.filters[chip.chipKey].sort = '';
            this.sortKey = '';
            this.sortOrder = '';
            this.tableService.setFilterTableSettings(this.tableId, {
              columns: this.activeChipFilters.filters,
              page: this.page,
              pageSize: this.pageSize,
            });
            this.fetchSitesList();
          }
        } else {
          // 2. Remove "filter chip" for chipKey with chipValue
          if (
            this.activeChipFilters.filters[chip.chipKey] &&
            Array.isArray(this.activeChipFilters.filters[chip.chipKey].filter)
          ) {
            this.activeChipFilters.filters[chip.chipKey].filter =
              this.activeChipFilters.filters[chip.chipKey].filter.filter(
                (val: any) => val !== chip.chipValue,
              );
            // Rebuild backend filters and update documents
            this.filters = this.buildBackendFiltersFromColumns(
              this.activeChipFilters.filters,
              this.columnDefinitions,
            );
            this.tableService.setFilterTableSettings(this.tableId, {
              columns: this.activeChipFilters.filters,
              page: this.page,
              pageSize: this.pageSize,
            });
            this.fetchSitesList();
          }
        }
      }
    }
  }

  buildBackendFiltersFromColumns(columns: any, columnDefinitions: any[]): any {
    const backendFilters: any = {};

    columnDefinitions.forEach((colDef) => {
      const key = colDef.key;
      const col = columns[key];
      if (
        col &&
        col.filter &&
        Array.isArray(col.filter) &&
        col.filter.length > 0
      ) {
        // Find options for this column if available
        const options = colDef.filter?.config?.options || [];
        const values = col.filter.map((val: any) => {
          // Try to find a matching option for key/label mapping
          const found = options.find(
            (opt: any) =>
              opt.value?.toString() === val?.toString() ||
              opt.key?.toString() === val?.toString() ||
              opt.label === val ||
              opt.name === val,
          );
          return found
            ? {
                key: found.key ?? found.value ?? val,
                label: found.label ?? found.name ?? val,
              }
            : { key: val, label: val };
        });

        backendFilters[key] = {
          isClientSide: false,
          type: 'multiSelect',
          values,
        };
      }
    });

    return backendFilters;
  }

  searchChange(searchText: string) {}

  fetchSitesList(): void {
    this.isTableLoading = true;
    if (this.utilityService.getTableSearchSetting(this.tableId)) {
      this.siteSearch = this.utilityService.getTableSearchSetting(this.tableId);
    }
    const listOptions = {
      Search: this.siteSearch ?? '',
      SortColumn: this.sortKey ?? '',
      SortDesc: this.sortOrder ?? '',
      PageSize: this.pageSize,
      Page: this.page,
      HasPaging: true,
      Filters: this.filters || {},
    };

    this.api.Sites.GetSites(listOptions).subscribe({
      next: (response: SiteListResponse) => {
        this.sitesResponse = response.Data ?? [];
        this.activeChipFilters.searchText = this.siteSearch ?? '';
        this.count = response.Count;
      },
      error: (error) => {
        console.error(error);
      },
      complete: () => {
        this.isTableLoading = false;
      },
    });
  }

  onRowClicked(rowData: Site) {
    this.router.navigate(['/site-details', { id: rowData.Id }]);
  }

  ngOnDestroy(): void {
    this.tableService.updateFilterTableSettings(this.tableId, {
      columns: this.activeChipFilters.filters,
    });
    this.destroy$.complete();
    this.searchInput.complete();
  }

  exportTable() {
    // console.log(this.siteTable.View);
    const listOptions = {
      Search: this.siteSearch ?? '',
      SortColumn: this.sortKey ?? '',
      SortDesc: this.sortOrder ?? '',
      PageSize: this.pageSize,
      Page: this.page,
      HasPaging: true,
      Filters: this.filters || {},
    };
    // const listOptions = this.siteTable.View;
    listOptions.Page = 0;
    listOptions.HasPaging = false;
    this.api.Sites.ExportSites(listOptions);
  }
}

export interface SiteListResponse {
  Count: number;
  Data: Site[];
}
